import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Table,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const CollectionList = () => {
  const { collectionName } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getCollection(collectionName, {
        'pagination[page]': 1,
        'pagination[pageSize]': 100,
        populate: '*',
      });
      const data = response.data || [];
      setItems(data);

      // Auto-redirect to edit form for 'lives' collection with single record
      if (collectionName === 'lives' && data.length === 1) {
        navigate(`/collections/${collectionName}/${data[0].documentId}`);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, [collectionName, navigate]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDelete = async () => {
    if (!deleteModal.item) return;

    try {
      await apiService.deleteCollectionItem(collectionName, deleteModal.item.documentId);
      setItems(items.filter((item) => item.documentId !== deleteModal.item.documentId));
      setDeleteModal({ show: false, item: null });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to delete item');
    }
  };

  const getDisplayValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) return `[${value.length} items]`;
      return '{object}';
    }
    const stringValue = String(value);
    return stringValue.length > 50 ? stringValue.substring(0, 50) + '...' : stringValue;
  };

  const getColumns = () => {
    if (items.length === 0) return [];
    const firstItem = items[0];
    const columns = ['documentId'];

    // Strapi v5: fields are directly on item, not in item.attributes
    const systemFields = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt', 'createdBy', 'updatedBy', 'locale'];
    const attrKeys = Object.keys(firstItem).filter(
      key => !systemFields.includes(key)
    );
    columns.push(...attrKeys.slice(0, 4)); // Show first 4 attributes

    return columns;
  };

  // Collection name translations
  const collectionNames = {
    'lives': 'البث المباشر',
    'videos': 'الفيديوهات',
    'categories': 'التصنيفات',
    'tags': 'الوسوم',
    'authors': 'الكتاب',
    'upload/files': 'الوسائط'
  };

  const collectionIcons = {
    'lives': 'bi-broadcast',
    'videos': 'bi-camera-video',
    'categories': 'bi-folder',
    'tags': 'bi-tags',
    'authors': 'bi-person-circle',
    'upload/files': 'bi-image'
  };

  if (loading) {
    return (
      <Layout title="جاري التحميل..." breadcrumbs={[]}>
        <Container className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </Spinner>
        </Container>
      </Layout>
    );
  }

  const columns = getColumns();
  const isViewOnly = collectionName === 'lives';

  const breadcrumbs = [
    { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
    { label: collectionNames[collectionName] || collectionName, icon: collectionIcons[collectionName] }
  ];

  return (
    <Layout title={collectionNames[collectionName] || collectionName} breadcrumbs={breadcrumbs}>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Badge bg="secondary" className="ms-2">
              <i className="bi bi-list-ul ms-1"></i>
              {items.length} عنصر
            </Badge>
            {isViewOnly && (
              <Badge bg="info" className="ms-2">
                <i className="bi bi-eye ms-1"></i>
                للعرض فقط
              </Badge>
            )}
          </div>
          {!isViewOnly && (
            <Button
              variant="primary"
              onClick={() => navigate(`/collections/${collectionName}/new`)}
            >
              <i className="bi bi-plus-circle ms-1"></i>
              إضافة جديد
            </Button>
          )}
        </div>

        {error && <Alert variant="danger" className="d-flex align-items-center">
          <i className="bi bi-exclamation-triangle ms-2"></i>
          {error}
        </Alert>}

        {items.length === 0 ? (
          <Alert variant="info" className="text-center">
            <i className="bi bi-info-circle ms-2"></i>
            {isViewOnly ? 'لا توجد عناصر.' : 'لا توجد عناصر. انقر على "إضافة جديد" لإنشاء العنصر الأول.'}
          </Alert>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="bg-white">
              <thead className="table-dark">
                <tr>
                  {columns.map((col) => (
                    <th key={col}>
                      {col === 'documentId' ? 'المعرف' : col}
                    </th>
                  ))}
                  {!isViewOnly && <th>الإجراءات</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.documentId}>
                    {columns.map((col) => (
                      <td key={col}>
                        {col === 'documentId'
                          ? item.documentId
                          : getDisplayValue(item[col])}
                      </td>
                    ))}
                    {!isViewOnly && (
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="ms-2"
                          onClick={() =>
                            navigate(`/collections/${collectionName}/${item.documentId}`)
                          }
                        >
                          <i className="bi bi-pencil ms-1"></i>
                          تعديل
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setDeleteModal({ show: true, item })}
                        >
                          <i className="bi bi-trash ms-1"></i>
                          حذف
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          show={deleteModal.show}
          onHide={() => setDeleteModal({ show: false, item: null })}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-exclamation-triangle ms-2"></i>
              تأكيد الحذف
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            هل أنت متأكد من حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ show: false, item: null })}
            >
              <i className="bi bi-x-circle ms-1"></i>
              إلغاء
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <i className="bi bi-trash ms-1"></i>
              حذف
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default CollectionList;
